import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Building2,
  Briefcase,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  ArrowRight,
} from 'lucide-react';
import api from '../../services/api';

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSellers: 0,
    totalBuyers: 0,
    totalIntroductions: 0,
    pendingIntroductions: 0,
    completedIntroductions: 0,
    recentActivity: [],
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load introduction stats
      const introResponse = await api.get('/introductions/stats');
      const introStats = introResponse.data?.data?.stats || {};
      const recentPending = introResponse.data?.data?.recentPending || [];

      // Load seller count
      const sellersResponse = await api.get('/sellers?limit=1');
      const totalSellers = sellersResponse.data?.data?.pagination?.totalCount || 0;

      // Load buyer count
      const buyersResponse = await api.get('/buyers?limit=1');
      const totalBuyers = buyersResponse.data?.data?.pagination?.totalCount || 0;

      setStats({
        totalSellers,
        totalBuyers,
        totalIntroductions: introStats.total || 0,
        pendingIntroductions: introStats.pending || 0,
        completedIntroductions: introStats.completed || 0,
        acceptedIntroductions: introStats.accepted || 0,
        declinedIntroductions: introStats.declined || 0,
        recentPending,
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600">Overview of Pharmunity platform activity</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Sellers"
          value={stats.totalSellers}
          icon={Building2}
          color="purple"
          description="Biotech companies"
        />
        <StatCard
          title="Total Buyers"
          value={stats.totalBuyers}
          icon={Briefcase}
          color="blue"
          description="Pharma BD executives"
        />
        <StatCard
          title="Pending Intros"
          value={stats.pendingIntroductions}
          icon={Clock}
          color="yellow"
          description="Awaiting review"
          link="/admin/introductions?status=pending"
        />
        <StatCard
          title="Completed Intros"
          value={stats.completedIntroductions}
          icon={CheckCircle}
          color="green"
          description="Successfully connected"
        />
      </div>

      {/* Introduction Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Introduction Overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Introduction Requests</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Pending</p>
                  <p className="text-sm text-gray-500">Awaiting admin review</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-yellow-600">{stats.pendingIntroductions}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Accepted</p>
                  <p className="text-sm text-gray-500">In progress</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-blue-600">{stats.acceptedIntroductions}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Completed</p>
                  <p className="text-sm text-gray-500">Successfully connected</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-green-600">{stats.completedIntroductions}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Declined</p>
                  <p className="text-sm text-gray-500">Not approved</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-red-600">{stats.declinedIntroductions}</span>
            </div>
          </div>

          <Link
            to="/admin/introductions"
            className="mt-6 flex items-center justify-center gap-2 w-full py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            View All Introductions
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Recent Pending Requests */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pending Requests</h2>
          {stats.recentPending && stats.recentPending.length > 0 ? (
            <div className="space-y-3">
              {stats.recentPending.map((request) => (
                <Link
                  key={request.id}
                  to={`/admin/introductions/${request.id}`}
                  className="block p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {request.request_type === 'seller_to_buyer'
                          ? `${request.seller_name} → ${request.buyer_name}`
                          : `${request.buyer_name} → ${request.seller_name}`
                        }
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-gray-500">No pending requests</p>
              <p className="text-sm text-gray-400">All introduction requests have been processed</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/admin/introductions?status=pending"
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Review Pending</p>
              <p className="text-sm text-gray-500">{stats.pendingIntroductions} requests waiting</p>
            </div>
          </Link>

          <Link
            to="/admin/users"
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Manage Users</p>
              <p className="text-sm text-gray-500">Add or edit admins</p>
            </div>
          </Link>

          <Link
            to="/admin/email"
            className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Email Settings</p>
              <p className="text-sm text-gray-500">Configure notifications</p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, description, link }) {
  const colorClasses = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
  };

  const content = (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6" />
        </div>
        {link && <ArrowRight className="w-5 h-5 text-gray-400" />}
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block hover:shadow-md transition-shadow rounded-xl">
        {content}
      </Link>
    );
  }

  return content;
}
